import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const SEOContent = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="w-full py-8 md:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div className="space-y-3">
            {/* Visible content */}
            <div className="space-y-2">
              <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                Assembly - The B2B HR SaaS Platform for Employee Recognition, Rewards & Culture Building
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                Assembly is a B2B HR SaaS platform that helps organizations of all sizes, from startups to global enterprises, recognize, reward, and retain employees effortlessly. As a scalable employee recognition and rewards software, Assembly connects people, performance, and purpose through an integrated suite of tools for engagement, analytics, and well-being. Whether your team is remote, hybrid, or on-site, Assembly ensures everyone feels seen, valued, and connected - in Slack, Microsoft Teams, BambooHR, and beyond.
              </p>
            </div>

            {/* Read More Button */}
            <CollapsibleTrigger className="flex items-center gap-2 text-xs md:text-sm text-primary hover:text-primary/80 transition-colors py-2">
              <span>{isOpen ? "Read Less" : "Read More"}</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </CollapsibleTrigger>

            {/* Hidden SEO Content */}
            <CollapsibleContent className="space-y-4 pt-2">
              <div className="space-y-2">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  What Is Assembly?
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Assembly is a cloud-based HR SaaS platform that simplifies employee recognition, rewards management, and culture measurement in one unified dashboard. It helps businesses automate appreciation, celebrate milestones, and analyze engagement patterns through AI-driven insights. Our platform fits seamlessly into daily workflows, helping HR leaders, managers, and employees alike recognize success in real time - without disrupting productivity.
                </p>
              </div>

              <div className="space-y-3">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  Who Can Use Assembly?
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Assembly adapts to your organization's scale and structure - from small teams experimenting with recognition to enterprises running global programs.
                </p>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    For 10–50 Employee Teams (Startups and Small Businesses)
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Start free and build a recognition culture from day one. Assembly's Free Plan allows small teams to experiment with appreciation, peer shoutouts, and rewards - no credit card required. Perfect for early-stage HR teams or founders who want to improve employee morale and retention quickly.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    For 50–200 Employee Teams (Scaling Organizations)
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    As your team grows, Assembly grows with you. Enable Teams or Slack-based recognition, automated birthdays, and milestone celebrations to keep everyone connected - even across time zones. Managers get AI nudges to recognize team wins, while HR leaders track engagement through Dora AI dashboards.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    For 200–500 Employee Teams (Mid-Market Businesses)
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    With more structure comes more opportunity to shape culture. Assembly helps mid-sized organizations standardize recognition frameworks, align recognition with company values, and automate performance-based rewards - all while integrating with HRIS tools like BambooHR and Workday.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    For 500–1000+ Employee Enterprises
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Large teams need visibility, scalability, and compliance. Assembly provides enterprise-grade security (SOC 2 Type II, GDPR), custom branding, and powerful analytics to support company-wide recognition at scale. Empower regional leaders and HR admins with localized dashboards and budget control.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  Where Can Assembly Be Used?
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Assembly works for all team configurations — whether you operate entirely online or in person.
                </p>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Remote Teams
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Keep distributed employees connected through peer recognition, shared wins, and automated celebrations. Assembly combats isolation by boosting employee well-being and belonging - all within Slack or Teams.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Hybrid Workforces
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Recognition flows across locations and time zones with Assembly's hybrid-friendly automations. Whether someone's at home or in the office, they'll see real-time appreciation from peers and managers.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    On-Site Teams
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Keep frontline, factory, or retail employees motivated with mobile-friendly recognition tools and automated milestone reminders. Supervisors can send shoutouts directly from their devices, improving motivation and reducing churn.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  How Does Assembly Work?
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Assembly integrates with your favorite workplace tools to make recognition a natural part of everyday communication. Connect your workspace - Slack, Microsoft Teams, or Web. Set up recognition categories based on company values. Empower employees to send shoutouts and earn points. Automate celebrations for birthdays, anniversaries, and wins. Redeem rewards from a global catalog or custom perks. Measure engagement with Dora AI analytics. No complex setup. No steep learning curve. Just instant culture lift.
                </p>
              </div>

              <div className="space-y-3">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  Why Choose Assembly Over Other HR Platforms
                </h2>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Simplicity Meets Power
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Assembly delivers enterprise-grade analytics in a user experience employees actually enjoy. Recognition happens in the tools they already use — Slack, Teams, BambooHR — ensuring 90%+ adoption.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Built for Real Connection
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    While others focus on transactions, we focus on behavioral transformation. Assembly builds habits of appreciation, creating long-term culture impact, not just one-time engagement spikes.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Security and Compliance
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    SOC 2 Type II certified and GDPR compliant, Assembly ensures data safety and global scalability.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Start Free — Upgrade Anytime
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Get started free, explore with your team, and upgrade seamlessly when ready. No long contracts. No hidden costs.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  When Should Companies Use Assembly?
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Any time you want to improve employee engagement and retention, reduce remote team burnout, strengthen company culture, reward and recognize performance automatically, or track culture ROI through data-driven insights. Whether you're launching your first recognition initiative or scaling globally, now is the time to start with Assembly.
                </p>
              </div>

              <div className="space-y-3">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  How Different Industries Use Assembly
                </h2>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Corporate & Finance
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Use Assembly to create transparent recognition systems that reward excellence and accountability. Track contributions and automate monetary or non-monetary rewards.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Healthcare
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Boost morale among medical and support staff through personalized appreciation and milestone tracking. Celebrate compassion, service, and teamwork - where it matters most.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Education
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Recognize faculty, administrators, and support staff through automated peer recognition and community celebrations. Foster belonging and motivation across campuses.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Manufacturing & Logistics
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Appreciate on-site and shift workers with quick shoutouts, digital badges, and safe performance incentives. Reward productivity and safety milestones to reduce turnover.
                  </p>
                </div>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Retail & Customer Service
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Keep your frontline employees inspired by recognizing great service moments in real time. Reward positive behaviors that lead to better customer experiences.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  Why Companies Trust Assembly
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Trusted by over 3,000+ global organizations. Rated 4.9/5 on G2 for ease of use and ROI. SOC 2 Type II certified and GDPR compliant. Backed by measurable engagement data. Used across industries — SaaS, Finance, Healthcare, Education, and Retail. Proven to reduce employee attrition by up to 60%.
                </p>
              </div>

              <div className="space-y-3">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  How Assembly Improves Employee Well-Being and Mental Health
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Recognition is one of the strongest predictors of employee happiness and mental wellness. When employees feel valued, their motivation, loyalty, and energy rise naturally. Assembly helps HR teams embed recognition into daily routines — fighting burnout, loneliness, and disengagement across remote and hybrid environments.
                </p>

                <div className="space-y-2 pl-2">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground leading-relaxed">
                    Results Our Clients Report
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    70% increase in overall happiness. 50% drop in burnout reports. 30% improvement in employee retention.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h2 className="text-sm md:text-base font-semibold text-foreground leading-relaxed">
                  Key Links for Quick Access
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Products: Recognition & Rewards • Milestones & Celebrations • Manager Tools • Culture Analytics (Dora AI). Solutions: Remote Teams • Hybrid Workplaces • Enterprise • Healthcare • Manufacturing. Resources: Blog • Pricing • Customer Stories • Security.
                </p>
              </div>

              <div className="pt-2">
                <p className="text-xs text-muted-foreground/70 leading-relaxed">
                  B2B HR SaaS platform • employee recognition software • rewards and engagement platform • HR automation tool • free employee recognition app • hybrid workforce engagement • remote employee well-being software • HR analytics platform • culture measurement tool • SOC 2 certified HR SaaS • employee retention software • mental health and engagement • Slack HR integration • Microsoft Teams recognition • HR software for hybrid workplaces • best employee engagement software for enterprises • recognition software for startups • AI-powered HR engagement platform.
                </p>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>
    </section>
  );
};

export default SEOContent;
